import React, { useState, useEffect } from 'react';
import { Button } from "@shared/ui/button";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useForm } from "react-hook-form";
import { FormField } from "@shared/services/FormServices";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { ArrowRight, ArrowLeft, CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { useResetPassword } from "../../../hooks";
import { useToast } from "@shared/ui/use-toast";
import { useTranslation } from 'react-i18next';

export const SetPassword = ({ onNext, onPrevious }: any) => {
  const { t } = useTranslation();
  const form = useForm({ mode: 'onChange' });
  const { mutate: resetPassword, isPending } = useResetPassword();
  const { toast } = useToast();

  const consumerNo = sessionStorage.getItem('create_account_consumer_no') || '';
  const firstName = sessionStorage.getItem('create_account_first_name') || '';
  const lastName = sessionStorage.getItem('create_account_last_name') || '';
  const accountType = sessionStorage.getItem('create_account_type') || '';
  const initial = firstName ? firstName.charAt(0).toUpperCase() : 'U';

  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordRequirements, setPasswordRequirements] = useState({
    hasMinLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasSpecialChar: false,
    hasDigit: false,
    passwordsMatch: false,
  });

  const password = form.watch("newPassword") || '';
  const confirmPassword = form.watch("confirmPassword") || '';

  useEffect(() => {
    const hasMinLength = password.length >= 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
    const hasDigit = /\d/.test(password);
    const passwordsMatch = password && confirmPassword && password === confirmPassword;

    setPasswordRequirements({
      hasMinLength,
      hasUppercase,
      hasLowercase,
      hasSpecialChar,
      hasDigit,
      passwordsMatch,
    });

    let strength = 0;
    if (hasMinLength) strength++;
    if (hasUppercase) strength++;
    if (hasLowercase) strength++;
    if (hasDigit) strength++;
    if (hasSpecialChar) strength++;

    setPasswordStrength(strength);
  }, [password, confirmPassword]);

  const onSubmit = (data: any) => {
    if (passwordStrength < 5 || data.newPassword !== data.confirmPassword) {
      return;
    }
    resetPassword(
      {
        consumer_no: sessionStorage.getItem('create_account_consumer_no') || '',
        remote_utility_id: Number(sessionStorage.getItem('create_account_remote_utility_id')) || 0,
        password: data.confirmPassword,
        is_registered_css: true
      },
      {
        onSuccess: () => {
          onNext();
        },
        onError: (error: any) => {
          toast({
            title: "Error",
            description: error?.message || "Failed to create account. Please try again.",
            variant: "destructive",
          });
        }
      }
    );
  };

  const fields: FormField[] = [
    { name: 'newPassword', label: t("Create Password"), type: 'password', required: true, placeholder: t("••••••••••••••••") },
  ];

  const confirmFields: FormField[] = [
    { name: 'confirmPassword', label: t("Confirm Password"), type: 'password', required: true, placeholder: t("Re-enter your password") }
  ];

  const getStrengthColor = (index: number) => {
    if (passwordStrength <= index) return 'bg-gray-200';
    if (passwordStrength <= 2) return 'bg-red-500';
    if (passwordStrength === 3) return 'bg-orange-500';
    if (passwordStrength === 4) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getStrengthText = () => {
    if (passwordStrength === 0) return '';
    if (passwordStrength <= 2) return t("Weak");
    if (passwordStrength === 3) return t("Fair");
    if (passwordStrength === 4) return t("Good");
    return t("Strong");
  };

  const getStrengthTextColor = () => {
    if (passwordStrength === 0) return 'text-transparent';
    if (passwordStrength <= 2) return 'text-red-600';
    if (passwordStrength === 3) return 'text-orange-600';
    if (passwordStrength === 4) return 'text-yellow-600';
    return 'text-green-600';
  };

  const renderFooter = () => (
    <>
      {confirmPassword && password && confirmPassword !== password && (
        <div className="flex items-center gap-2 p-3 mt-4 text-red-600 bg-red-50 rounded-xl border border-red-100 text-sm">
          <AlertCircle className="w-4 h-4" /> {t("Passwords do not match")}
        </div>
      )}
      <div className="flex items-center gap-3 mt-6">
        <Button
          type="button"
          variant="outline"
          onClick={onPrevious}
          className=""
        >
          <ArrowLeft className="h-4 w-4 mr-2" /> {t("Back")}
        </Button>
        <Button
          type="button"
          variant="default"
          onClick={form.handleSubmit(onSubmit)}
          className="w-full"
          disabled={passwordStrength < 5 || !passwordRequirements.passwordsMatch || isPending}
        >
          {isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t("Creating...")}
            </>
          ) : (
            <>
              {t("Create Account")} <ArrowRight className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
      </div>
    </>
  );

  return (
    <div className="flex flex-col p-2">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">{t("Create your password")}</h2>
      <p className="text-gray-500 text-sm mb-6">{t("Choose a strong password to protect your utility account.")}</p>

      <DynamicForm
        fields={fields}
        form={form}
        onSubmit={() => { }}
        config={{ classes: FIELD_CLASSES, showSubmitButton: false }}
      />

      {/* Password Strength Indicator */}
      {password && (
        <div className="mb-6 mt-2">
          <div className="flex gap-1 mb-2">
            {[0, 1, 2, 3, 4].map(i => (
              <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${getStrengthColor(i)}`}></div>
            ))}
          </div>
          <div className={`text-sm font-medium mb-3 min-h-[20px] ${getStrengthTextColor()}`}>{getStrengthText()}</div>

          <div className="space-y-2">
            <div className={`flex items-center text-sm transition-colors ${passwordRequirements.hasMinLength ? 'text-green-600' : 'text-gray-500'}`}>
              {passwordRequirements.hasMinLength ? <CheckCircle2 className="h-4 w-4 mr-2" /> : <div className="h-4 w-4 mr-2 rounded-full border border-gray-300"></div>}
              {t("At least 8 characters")}
            </div>
            <div className={`flex items-center text-sm transition-colors ${passwordRequirements.hasUppercase ? 'text-green-600' : 'text-gray-500'}`}>
              {passwordRequirements.hasUppercase ? <CheckCircle2 className="h-4 w-4 mr-2" /> : <div className="h-4 w-4 mr-2 rounded-full border border-gray-300"></div>}
              {t("One uppercase letter (A-Z)")}
            </div>
            <div className={`flex items-center text-sm transition-colors ${passwordRequirements.hasLowercase ? 'text-green-600' : 'text-gray-500'}`}>
              {passwordRequirements.hasLowercase ? <CheckCircle2 className="h-4 w-4 mr-2" /> : <div className="h-4 w-4 mr-2 rounded-full border border-gray-300"></div>}
              {t("One lowercase letter (a-z)")}
            </div>
            <div className={`flex items-center text-sm transition-colors ${passwordRequirements.hasDigit ? 'text-green-600' : 'text-gray-500'}`}>
              {passwordRequirements.hasDigit ? <CheckCircle2 className="h-4 w-4 mr-2" /> : <div className="h-4 w-4 mr-2 rounded-full border border-gray-300"></div>}
              {t("One number (0-9)")}
            </div>
            <div className={`flex items-center text-sm transition-colors ${passwordRequirements.hasSpecialChar ? 'text-green-600' : 'text-gray-500'}`}>
              {passwordRequirements.hasSpecialChar ? <CheckCircle2 className="h-4 w-4 mr-2" /> : <div className="h-4 w-4 mr-2 rounded-full border border-gray-300"></div>}
              {t("One special character (!@#$)")}
            </div>
          </div>
        </div>
      )}

      <DynamicForm
        fields={confirmFields}
        form={form}
        onSubmit={onSubmit}
        config={{ classes: FIELD_CLASSES, showSubmitButton: false }}
        renderCustomFooter={renderFooter}
      />

      <div className="bg-white border border-gray-200 rounded-xl p-4 mt-6">
        <h3 className="text-[10px] font-bold text-gray-400  tracking-wider mb-3">{t("Account Being Created")}</h3>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white font-bold">
            {initial}
          </div>
          <div>
            <div className="font-bold text-gray-900 text-sm">{firstName} {lastName}</div>
            <div className="text-xs text-gray-500">{consumerNo} · {accountType}</div>
          </div>
        </div>
      </div>
    </div>
  );
};
